import { SubscriptionNoticeBannerVariant } from '@/account/components/subscription-notice-banner/SubscriptionNoticeBanner'
import { ArtifactEditorEntryPoint, ArtifactEditorFormatType } from '@/analytics/enums/artifact-editor.enums'
import { DictationFailure, DictationSurface } from '@/analytics/enums/dictation.enums'
import { InlineEditResolvedAction } from '@/analytics/enums/inline-edit.enums'
import { MatterBannerKind } from '@/matter/store/schemas'
import { StripeSubscriptionInterval } from '@/account/store/schemas'
// Domain-specific enums
import { RiaToggleState, RiaDialogSource, RiaToggleName, RiaTab, RiaSourceTriggerLocation, RiaViewerTab, RiaCitatorSection } from '@/analytics/enums/ria.enums'
import { CitatorBadgeType, CitatorOpenedIn, CitatorSurface, CitatorPanelSource, CitatorTreatmentOutcome } from '@/analytics/enums/citator.enums'
import { ArtifactTemplateSource, ArtifactExportType, ArtifactTemplateApplyErrorType } from '@/analytics/enums/artifact-template.enums'
import { DocxDownloadSurface, DocxFolderSource } from '@/analytics/enums/docx-export.enums'
import { DraftEntryPoint } from '@/analytics/enums/draft.enums'
import { MatterRecordEntryPoint, MatterRecordType } from '@/analytics/enums/matter-record.enums'
import { SkillDownloadSurface, SkillOrigin } from '@/analytics/enums/skill.enums'
import { MatterDriveSyncDismissReason, MatterDriveSyncEntrySource } from '@/analytics/enums/matter-drive-sync.enums'
import { MatterJurisdictionEntrySource } from '@/analytics/enums/matter-jurisdiction.enums'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { OrganizationJoinPath, OrganizationRosterEntrySource } from '@/analytics/enums/organization.enums'
import { SettingsEntrySource } from '@/analytics/enums/settings-entry.enums'
import { SettingsModalSection, SettingsViewTrigger } from '@/analytics/enums/settings-modal.enums'
import { SidebarLink } from '@/analytics/enums/sidebar.enums'
import {
  TableCreationMethod,
  TableDocumentSource,
  TableDownloadScope,
  TableCopyMethod,
  TableEditAction,
  TableEditSource,
  TableEntrySource,
  TableFilterMatch,
} from '@/analytics/enums/table.enums'
import { UnsupportedUploadMediaKind } from '@/analytics/enums/unsupported-upload.enums'
import { AppUpdateSource } from '@/analytics/enums/app-update.enums'
import type { ExternalStorageProvider } from '@/constants/constants-external-storage'

export enum SkillSource {
  modal = 'modal',
}

// Template Source (where the template was added from)
export enum TemplateSource {
  upload = 'upload',
  drive = 'drive',
}

export enum AnalyticsEvent {
  SignUp = 'SignUp',
  PaymentMethodAdded = 'PaymentMethodAdded',
  PaymentMethodAddedMonthly = 'PaymentMethodAddedMonthly',
  PaymentMethodAddedAnnual = 'PaymentMethodAddedAnnual',
  NewChat = 'NewChat',
  NewChatMessage = 'NewChatMessage',
  DictationStarted = 'DictationStarted',
  DictationCompleted = 'DictationCompleted',
  DictationFailed = 'DictationFailed',
  OpenExistingConversation = 'OpenExistingConversation',
  OpenConversationDeletionDialog = 'OpenConversationDeletionDialog',
  DownloadConversationToDocx = 'DownloadConversationToDocx',
  OpenConversationRetitleDialog = 'OpenConversationRetitleDialog',
  CopyChatResponse = 'CopyChatResponse',
  CopyChatUserMessage = 'CopyChatUserMessage',
  // Outlook add-in: the reply-review Copy button put the drafted reply on the clipboard
  OutlookReplyCopied = 'OutlookReplyCopied',
  DownloadChatSourceToDocx = 'DownloadChatSourceToDocx',
  ReferralDialogOpened = 'ReferralDialogOpened',
  ReferralLinkCopied = 'ReferralLinkCopied',
  ReferralLinkCopiedFromInput = 'ReferralLinkCopiedFromInput',
  MatterCreated = 'MatterCreated',
  MatterUpdated = 'MatterUpdated',
  MatterDeleted = 'MatterDeleted',
  MatterInviteSent = 'MatterInviteSent',
  MatterInviteSendFailed = 'MatterInviteSendFailed',
  MatterInviteAccepted = 'MatterInviteAccepted',
  MatterInviteAcceptFailed = 'MatterInviteAcceptFailed',
  MatterInviteRejected = 'MatterInviteRejected',
  MatterInviteRejectFailed = 'MatterInviteRejectFailed',
  MatterSelectionChanged = 'MatterSelectionChanged',
  MatterCollaborationBannerAddUserClicked = 'MatterCollaborationBannerAddUserClicked',
  MatterBannerDismissed = 'MatterBannerDismissed',
  MatterCollaborationHeaderAddUserClicked = 'MatterCollaborationHeaderAddUserClicked',
  MatterManageMembersModalOpened = 'MatterManageMembersModalOpened',
  OnboardingFormSubmitted = 'OnboardingFormSubmitted',
  SkillCopied = 'SkillCopied',
  SkillDownloaded = 'SkillDownloaded',
  SkillCreated = 'SkillCreated',
  SkillEdited = 'SkillEdited',
  SkillDeleted = 'SkillDeleted',
  SubscriptionNoticeBannerDismissed = 'SubscriptionNoticeBannerDismissed',
  SubscriptionNoticeBannerCTAClicked = 'SubscriptionNoticeBannerCTAClicked',
  StripePortalOpened = 'StripePortalOpened',
  AppUpdateRefreshClicked = 'AppUpdateRefreshClicked',
  SideNavToggled = 'SideNavToggled',
  SidebarLinkClicked = 'SidebarLinkClicked',
  ClipboardCopy = 'ClipboardCopy',
  ApplyTemplateTabOpened = 'ApplyTemplateTabOpened',
  ApplyTemplateTabClosed = 'ApplyTemplateTabClosed',
  TemplateSourceChosen = 'TemplateSourceChosen',
  TemplateDriveDialogOpened = 'TemplateDriveDialogOpened',
  TemplateSourceDialogDismissed = 'TemplateSourceDialogDismissed',
  TemplateInfluencedFeedback = 'TemplateInfluencedFeedback',
  ClipboardPaste = 'ClipboardPaste',
  ResponseThumbUp = 'ResponseThumbUp',
  ResponseThumbDown = 'ResponseThumbDown',
  ArtifactClipboardCopy = 'ArtifactClipboardCopy',
  ArtifactEditorOpened = 'ArtifactEditorOpened',
  ArtifactEditorFirstEdit = 'ArtifactEditorFirstEdit',
  ArtifactEditorSessionCompleted = 'ArtifactEditorSessionCompleted',
  ArtifactEditorAutoSaveSuccess = 'ArtifactEditorAutoSaveSuccess',
  ArtifactEditorFormattingUsed = 'ArtifactEditorFormattingUsed',
  ArtifactGenerationCompleted = 'ArtifactGenerationCompleted',
  InlineEditSubmitted = 'InlineEditSubmitted',
  InlineEditCompleted = 'InlineEditCompleted',
  InlineEditResolved = 'InlineEditResolved',
  OrganizationCreationCtaClicked = 'OrganizationCreationCtaClicked',
  OrganizationCreated = 'OrganizationCreated',
  OrganizationInviteAccepted = 'OrganizationInviteAccepted',
  OrganizationInviteSent = 'OrganizationInviteSent',
  OrganizationUserRemoved = 'OrganizationUserRemoved',
  OrganizationRosterOpened = 'OrganizationRosterOpened',
  OrganizationLeft = 'OrganizationLeft',
  // Medical Billing Summary – Living asset (single matter billing summary)
  MedSummaryAssetGenerateClicked = 'MedSummaryAssetGenerateClicked',
  MedSummaryAssetUpdateClicked = 'MedSummaryAssetUpdateClicked',
  MedSummaryAssetRetryFailedClicked = 'MedSummaryAssetRetryFailedClicked',
  MedSummaryAssetCompleteExcludingFailedClicked = 'MedSummaryAssetCompleteExcludingFailedClicked',
  MedSummaryAssetFileRemoved = 'MedSummaryAssetFileRemoved',
  MedSummaryAssetChatClicked = 'MedSummaryAssetChatClicked',
  MedSummaryAssetDownloadClicked = 'MedSummaryAssetDownloadClicked',
  MedSummaryAssetReferenceViewerOpened = 'MedSummaryAssetReferenceViewerOpened',
  MedSummaryAssetEntryFieldSaved = 'MedSummaryAssetEntryFieldSaved',
  MedSummaryAssetEntryFieldSaveFailed = 'MedSummaryAssetEntryFieldSaveFailed',
  // Medical Chronology – Living asset (single matter chronology)
  MedChronAssetChatClicked = 'MedChronAssetChatClicked',
  MedChronAssetDownloadClicked = 'MedChronAssetDownloadClicked',
  MedChronAssetGenerateClicked = 'MedChronAssetGenerateClicked',
  MedChronAssetUpdateClicked = 'MedChronAssetUpdateClicked',
  MedChronAssetFileRemoved = 'MedChronAssetFileRemoved',
  MedChronAssetReferenceViewerOpened = 'MedChronAssetReferenceViewerOpened',
  // Timeline (Single Matter) – Living asset (single matter timeline)
  TimelineSourceOpenFailed = 'TimelineSourceOpenFailed',
  TimelineAssetReferenceViewerOpened = 'TimelineAssetReferenceViewerOpened',
  TimelineAssetDownloadClicked = 'TimelineAssetDownloadClicked',
  TimelineAssetGenerateClicked = 'TimelineAssetGenerateClicked',
  TimelineAssetUpdateClicked = 'TimelineAssetUpdateClicked',
  TimelineAssetFileRemoved = 'TimelineAssetFileRemoved',
  TimelineAssetChatClicked = 'TimelineAssetChatClicked',
  // Matter Records – Build a new record, before a living asset is generated
  MatterRecordBuildClicked = 'MatterRecordBuildClicked',
  MatterRecordTypeSelected = 'MatterRecordTypeSelected',
  MatterRecordBuildContinued = 'MatterRecordBuildContinued',
  MatterRecordExistingOpened = 'MatterRecordExistingOpened',
  // Research in Assistant – Discovery & Intent
  RiaToggleClicked = 'RiaToggleClicked',
  RiaSourcesButtonClicked = 'RiaSourcesButtonClicked',
  RiaSourcesDialogOpened = 'RiaSourcesDialogOpened',
  // Research in Assistant – Configuration & Submission
  RiaSourcesTabChanged = 'RiaSourcesTabChanged',
  RiaSourcesClearAllClicked = 'RiaSourcesClearAllClicked',
  RiaSourcesApplyClicked = 'RiaSourcesApplyClicked',
  RiaSelectAllFederalChecked = 'RiaSelectAllFederalChecked',
  RiaSelectAllFederalUnchecked = 'RiaSelectAllFederalUnchecked',
  // Research in Assistant – Usage & Engagement
  RiaMessageWithResearchSubmitted = 'RiaMessageWithResearchSubmitted',
  RiaSourceDownloadClicked = 'RiaSourceDownloadClicked',
  RiaSourceViewerTabChanged = 'RiaSourceViewerTabChanged',
  RiaCaseAnalysisRequested = 'RiaCaseAnalysisRequested',
  RiaCitatorSectionExpanded = 'RiaCitatorSectionExpanded',
  RiaCitatorCaseSelected = 'RiaCitatorCaseSelected',
  RiaCitatorRefreshClicked = 'RiaCitatorRefreshClicked',
  RiaSourceOpenInNewTabClicked = 'RiaSourceOpenInNewTabClicked',
  // AI Citator – Case treatment badge engagement
  CitatorBadgeImpression = 'CitatorBadgeImpression',
  CitatorBadgeClicked = 'CitatorBadgeClicked',
  CitatorPanelOpened = 'CitatorPanelOpened',
  CitatorTreatmentOutcomeClicked = 'CitatorTreatmentOutcomeClicked',
  CitatorReferenceGroupExpanded = 'CitatorReferenceGroupExpanded',
  // High-Fidelity Template Adherence – apply template at export (canvas artifacts)
  ArtifactTemplateSelected = 'ArtifactTemplateSelected',
  ArtifactTemplateApplyCompleted = 'ArtifactTemplateApplyCompleted',
  ArtifactTemplateApplyFailed = 'ArtifactTemplateApplyFailed',
  ArtifactDownloadSuccess = 'ArtifactDownloadSuccess',
  ArtifactDownloadFailed = 'ArtifactDownloadFailed',
  ArtifactExportCompleted = 'ArtifactExportCompleted',
  // Export as .docx actions only the browser sees. The export row records admission and completion.
  DocxExportDownloaded = 'DocxExportDownloaded',
  DocxExportsFolderOpened = 'DocxExportsFolderOpened',
  // Drafting entry points (PRD: Drafting Entry Points & Document-First) — re-entering an existing draft
  DraftEntryPointClicked = 'DraftEntryPointClicked',
  // Settings modal. The section on screen, including the first section of an open.
  SettingsModalViewed = 'SettingsModalViewed',
  // Jurisdiction in Matters – adoption nudges on existing matters
  DefaultJurisdictionConfigureClicked = 'DefaultJurisdictionConfigureClicked',
  DefaultJurisdictionSaved = 'DefaultJurisdictionSaved',

  // Custom Instructions - user-scoped drafting preferences (ENG-7323)
  CustomInstructionsSaved = 'CustomInstructionsSaved',
  MatterJurisdictionTooltipLinkClicked = 'MatterJurisdictionTooltipLinkClicked',
  MatterJurisdictionCtaViewed = 'MatterJurisdictionCtaViewed',
  MatterJurisdictionCtaClicked = 'MatterJurisdictionCtaClicked',
  // Continuous Matter Drive Sync – connect/map/disconnect DMS folders (DAT-12)
  MatterDriveSyncSetupEntryClicked = 'MatterDriveSyncSetupEntryClicked',
  MatterDriveSyncDiscoveryClicked = 'MatterDriveSyncDiscoveryClicked',
  MatterDriveSyncAccountConnectModalOpened = 'MatterDriveSyncAccountConnectModalOpened',
  MatterDriveSyncConnectRedirectStarted = 'MatterDriveSyncConnectRedirectStarted',
  MatterDriveSyncConnectRedirectFailed = 'MatterDriveSyncConnectRedirectFailed',
  MatterDriveSyncAccountConnected = 'MatterDriveSyncAccountConnected',
  MatterDriveSyncAccountDisconnected = 'MatterDriveSyncAccountDisconnected',
  MatterDriveSyncAccountDisconnectFailed = 'MatterDriveSyncAccountDisconnectFailed',
  MatterDriveSyncFolderPickerOpened = 'MatterDriveSyncFolderPickerOpened',
  MatterDriveSyncFolderBrowseFailed = 'MatterDriveSyncFolderBrowseFailed',
  MatterDriveSyncFolderPickerDismissed = 'MatterDriveSyncFolderPickerDismissed',
  MatterDriveSyncFolderMapped = 'MatterDriveSyncFolderMapped',
  MatterDriveSyncFolderMapFailed = 'MatterDriveSyncFolderMapFailed',
  MatterDriveSyncFolderDisconnected = 'MatterDriveSyncFolderDisconnected',
  MatterDriveSyncFolderDisconnectFailed = 'MatterDriveSyncFolderDisconnectFailed',
  // Client-side user-files upload rejection before Uppy/backend (DAT-26)
  UserFileUnsupportedTypeRejected = 'UserFileUnsupportedTypeRejected',
  TableCreated = 'TableCreated',
  TableOpened = 'TableOpened',
  TableRenamed = 'TableRenamed',
  TableDeleted = 'TableDeleted',
  TableEdited = 'TableEdited',
  TableDocumentsAdded = 'TableDocumentsAdded',
  TableFiltersApplied = 'TableFiltersApplied',
  TableDownloaded = 'TableDownloaded',
  TableCopyRequested = 'TableCopyRequested',
  TableColumnHidden = 'TableColumnHidden',
  TableColumnsClicked = 'TableColumnsClicked',
  TableSortClicked = 'TableSortClicked',
  TableAssistantOpened = 'TableAssistantOpened',
  TableAssistantClosed = 'TableAssistantClosed',
  TableReferenceClicked = 'TableReferenceClicked',

  // Matter-file citation highlighting: how well a citation's passages were highlighted when it opened
  ReferenceHighlightMatchStatus = 'ReferenceHighlightMatchStatus',
}

export type AnalyticsEventProperties = {
  [AnalyticsEvent.SignUp]: object
  [AnalyticsEvent.PaymentMethodAdded]: {
    subscriptionInterval?: string
  }
  [AnalyticsEvent.NewChat]: {
    conversationMatterId?: string
  }
  [AnalyticsEvent.NewChatMessage]: {
    conversationMatterId?: string
    riaResearchEnabled?: boolean
    dictationUsed: boolean
    webSearchEnabled: boolean
    userSkillCount: number
    paxtonSkillCount: number
    // Comma-separated Paxton skill names. The data layer does not accept an array.
    paxtonSkillsUsed: string
    // True when the message names a user skill. This is a text heuristic, not an attachment, and it does not identify which skill. The skill id and name stay off the payload.
    userSkillInvoked: boolean
    // Backend turn id. The same value the server stores as turn_id. The event fires once the turn is accepted.
    turnId: string
    // Set when the send is from the table assistant. Other sends omit it.
    tableId?: string
  }
  [AnalyticsEvent.DictationStarted]: {
    dictationSurface: DictationSurface
    conversationId?: string
    composerHadText: boolean
  }
  [AnalyticsEvent.DictationCompleted]: {
    dictationSurface: DictationSurface
    conversationId?: string
    composerHadText: boolean
    durationMs: number
  }
  [AnalyticsEvent.DictationFailed]: {
    dictationSurface: DictationSurface
    conversationId?: string
    dictationFailure: DictationFailure
    durationMs?: number
  }
  [AnalyticsEvent.OpenExistingConversation]: object
  [AnalyticsEvent.OpenConversationDeletionDialog]: object
  [AnalyticsEvent.DownloadConversationToDocx]: object
  [AnalyticsEvent.OpenConversationRetitleDialog]: object
  [AnalyticsEvent.CopyChatResponse]: object
  [AnalyticsEvent.CopyChatUserMessage]: object
  // Emitted only after the clipboard write succeeds. Never carries email or reply text.
  [AnalyticsEvent.OutlookReplyCopied]: {
    conversationId: string
  }
  [AnalyticsEvent.DownloadChatSourceToDocx]: object
  [AnalyticsEvent.ReferralDialogOpened]: object
  [AnalyticsEvent.ReferralLinkCopied]: object
  [AnalyticsEvent.ReferralLinkCopiedFromInput]: object
  [AnalyticsEvent.PaymentMethodAddedMonthly]: object
  [AnalyticsEvent.PaymentMethodAddedAnnual]: object
  [AnalyticsEvent.MatterCreated]: {
    matterJurisdictionId: string
    isStateMatterJurisdiction: boolean
    isFederalMatterJurisdiction: boolean
    isMatterJurisdictionUndecided: boolean
    matterStateJurisdiction: string
    matterFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterUpdated]: {
    matterNameChanged: boolean
    matterClientChanged: boolean
    matterDescriptionChanged: boolean
    matterJurisdictionEntrySource: MatterJurisdictionEntrySource
    matterJurisdictionChanged: boolean
    matterJurisdictionAdopted: boolean
    matterJurisdictionId: string
    isStateMatterJurisdiction: boolean
    isFederalMatterJurisdiction: boolean
    isMatterJurisdictionUndecided: boolean
    matterStateJurisdiction: string
    matterFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterDeleted]: object
  [AnalyticsEvent.MatterInviteSent]: {
    matterId: string
    organizationId: string
    recipientUserId: string
    selectedRecipientCount: number
    invitedCount: number
    matterInviteEntrySource: MatterCollaborationEntrySource
  }
  [AnalyticsEvent.MatterInviteSendFailed]: {
    matterId: string
    organizationId: string
    selectedRecipientCount: number
    errorType: string
    statusCode?: number
    matterInviteEntrySource: MatterCollaborationEntrySource
  }
  [AnalyticsEvent.MatterInviteAccepted]: {
    matterId: string
    organizationId: string
  }
  [AnalyticsEvent.MatterInviteAcceptFailed]: {
    matterId: string
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterInviteRejected]: {
    matterId: string
    organizationId: string
  }
  [AnalyticsEvent.MatterInviteRejectFailed]: {
    matterId: string
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterSelectionChanged]: {
    selectedMatterId: string
  }
  [AnalyticsEvent.MatterCollaborationBannerAddUserClicked]: {
    matterId: string
    organizationId: string
    // Account org membership at click time — distinguishes painted-door (false) from Manage Members (true).
    isOrgAccount: boolean
  }
  [AnalyticsEvent.MatterBannerDismissed]: {
    matterId: string
    organizationId: string
    matterBannerKind: MatterBannerKind
  }
  [AnalyticsEvent.MatterCollaborationHeaderAddUserClicked]: {
    matterId: string
    organizationId: string
  }
  [AnalyticsEvent.MatterManageMembersModalOpened]: {
    matterId: string
    organizationId: string
    matterInviteEntrySource: MatterCollaborationEntrySource
  }
  [AnalyticsEvent.OnboardingFormSubmitted]: {
    firstName: string
    lastName: string
    professionalRole: string
    areaOfPractice: string
    firmSize: string
    rankingFirstChoice: string
    rankingSecondChoice: string
    rankingThirdChoice: string
    paxtonReferrer: string
    socialNetwork: string
    professionalRoleOther: string
    areaOfPracticeOther: string
    rankingFirstChoiceOther: string
    rankingSecondChoiceOther: string
    rankingThirdChoiceOther: string
    paxtonReferrerOther: string
  }
  [AnalyticsEvent.SkillCopied]: {
    skillOrigin: SkillOrigin
    // User-skill id. Absent for a Paxton skill. The name stays off the payload.
    skillId?: string
  }
  [AnalyticsEvent.SkillDownloaded]: {
    skillOrigin: SkillOrigin
    skillDownloadSurface: SkillDownloadSurface
    skillId: string
  }
  [AnalyticsEvent.SkillCreated]: {
    skillId: string
  }
  [AnalyticsEvent.SkillEdited]: {
    skillId: string
  }
  [AnalyticsEvent.SkillDeleted]: {
    skillId: string
  }
  [AnalyticsEvent.SubscriptionNoticeBannerDismissed]: {
    subscriptionNoticeBannerVariant: SubscriptionNoticeBannerVariant
    subscriptionInterval: StripeSubscriptionInterval
  }
  [AnalyticsEvent.SubscriptionNoticeBannerCTAClicked]: {
    subscriptionNoticeBannerVariant: SubscriptionNoticeBannerVariant
    subscriptionInterval: StripeSubscriptionInterval
  }
  [AnalyticsEvent.StripePortalOpened]: object
  [AnalyticsEvent.AppUpdateRefreshClicked]: {
    appVersion: string
    appUpdateSource: AppUpdateSource
  }
  [AnalyticsEvent.SideNavToggled]: {
    sideNavCollapsed: boolean
  }
  [AnalyticsEvent.SidebarLinkClicked]: {
    sidebarLink: SidebarLink
    sideNavCollapsed: boolean
  }
  [AnalyticsEvent.ClipboardCopy]: {
    conversationMatterId?: string
    skill?: string
    skillSource?: SkillSource
  }
  [AnalyticsEvent.ClipboardPaste]: {
    conversationMatterId?: string
    skill?: string
    skillSource?: SkillSource
  }
  [AnalyticsEvent.ResponseThumbUp]: {
    skill?: string
    skillSource?: string
  }
  [AnalyticsEvent.ResponseThumbDown]: {
    skill?: string
    skillSource?: string
  }
  [AnalyticsEvent.ArtifactClipboardCopy]: {
    artifactId: string
    artifactVersionId: string
    sessionId: string
    matterId?: string
  }
  [AnalyticsEvent.ArtifactEditorOpened]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    artifactType?: string
    artifactEditorEntryPoint: ArtifactEditorEntryPoint
  }
  [AnalyticsEvent.ArtifactEditorFirstEdit]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    timeToFirstEdit: number
  }
  [AnalyticsEvent.ArtifactEditorSessionCompleted]: {
    sessionDuration: number
    finalContentLength: number
    saveCount: number
    formatActionsUsed: number
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
  }
  [AnalyticsEvent.ArtifactEditorAutoSaveSuccess]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
    saveTimestamp: number
    contentLength: number
  }
  [AnalyticsEvent.ArtifactEditorFormattingUsed]: {
    formatType: ArtifactEditorFormatType
    artifactId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    matterId: string
  }
  [AnalyticsEvent.ArtifactGenerationCompleted]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    matterId: string
  }
  [AnalyticsEvent.InlineEditSubmitted]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    selectionWordCount: number
    selectionFrom: number
    selectionTo: number
    isPreset: boolean
  }
  [AnalyticsEvent.InlineEditCompleted]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    inlineEditSuccess: boolean
    durationMs: number
    errorType?: string
  }
  [AnalyticsEvent.InlineEditResolved]: {
    inlineEditId: string
    artifactVersionId: string
    conversationId: string
    sessionId: string
    inlineEditResolution: InlineEditResolvedAction
  }
  // Medical Billing Summary – Living asset (single matter billing summary)
  [AnalyticsEvent.MedSummaryAssetGenerateClicked]: {
    matterId: string
    medSummarySelectedFilesCount: number
  }
  [AnalyticsEvent.MedSummaryAssetUpdateClicked]: {
    medSummaryAssetId: string
    medSummarySelectedFilesCount: number
  }
  [AnalyticsEvent.MedSummaryAssetRetryFailedClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetCompleteExcludingFailedClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetFileRemoved]: {
    medSummaryAssetId: string
    medSummaryEntriesRemoved: number
  }
  [AnalyticsEvent.MedSummaryAssetChatClicked]: {
    medSummaryAssetId: string
    matterId: string
  }
  [AnalyticsEvent.MedSummaryAssetDownloadClicked]: {
    medSummaryAssetId: string
  }
  [AnalyticsEvent.MedSummaryAssetReferenceViewerOpened]: {
    medSummaryAssetId: string
    medSummaryReferenceId: string
  }
  [AnalyticsEvent.MedSummaryAssetEntryFieldSaved]: {
    medSummaryAssetId: string
    medSummaryEntryId: string
    medSummaryEntryField: string
  }
  [AnalyticsEvent.MedSummaryAssetEntryFieldSaveFailed]: {
    medSummaryAssetId: string
    medSummaryEntryId: string
    medSummaryEntryField: string
    errorType: string
    statusCode?: number
  }
  // Medical Chronology – Living asset (single matter chronology)
  [AnalyticsEvent.MedChronAssetChatClicked]: {
    medChronAssetId: string
    matterId: string
  }
  [AnalyticsEvent.MedChronAssetDownloadClicked]: {
    medChronAssetId: string
  }
  [AnalyticsEvent.MedChronAssetGenerateClicked]: {
    matterId: string
    medChronSelectedFilesCount: number
  }
  [AnalyticsEvent.MedChronAssetUpdateClicked]: {
    medChronAssetId: string
    medChronSelectedFilesCount: number
  }
  [AnalyticsEvent.MedChronAssetFileRemoved]: {
    medChronAssetId: string
    medChronEventsRemoved: number
  }
  [AnalyticsEvent.MedChronAssetReferenceViewerOpened]: {
    medChronAssetId: string
    medChronReferenceId: string
  }
  // Timeline (Single Matter) – Living asset
  [AnalyticsEvent.TimelineSourceOpenFailed]: {
    timelineSourceId: string
    timelineJobId?: string
  }
  [AnalyticsEvent.TimelineAssetReferenceViewerOpened]: {
    timelineAssetId: string
    timelineReferenceId: string
  }
  [AnalyticsEvent.TimelineAssetDownloadClicked]: {
    timelineAssetId: string
  }
  [AnalyticsEvent.TimelineAssetGenerateClicked]: {
    matterId: string
    timelineSelectedFilesCount: number
  }
  [AnalyticsEvent.TimelineAssetUpdateClicked]: {
    timelineAssetId: string
    timelineSelectedFilesCount: number
  }
  [AnalyticsEvent.TimelineAssetFileRemoved]: {
    timelineAssetId: string
    timelineEventsRemoved: number
  }
  [AnalyticsEvent.TimelineAssetChatClicked]: {
    timelineAssetId: string
    matterId: string
  }
  // Build a new record. feature is not on the payload; callers suppress route inheritance.
  // *GenerateClicked stays the later generate click and does not gain matterRecordType.
  [AnalyticsEvent.MatterRecordBuildClicked]: {
    matterId: string
  }
  [AnalyticsEvent.MatterRecordTypeSelected]: {
    matterId: string
    matterRecordType: MatterRecordType
  }
  [AnalyticsEvent.MatterRecordBuildContinued]: {
    matterId: string
    matterRecordType: MatterRecordType
  }
  [AnalyticsEvent.MatterRecordExistingOpened]: {
    matterId: string
    matterRecordType: MatterRecordType
    matterRecordEntryPoint: MatterRecordEntryPoint
  }
  [AnalyticsEvent.ApplyTemplateTabOpened]: {
    conversationId: string
  }
  [AnalyticsEvent.ApplyTemplateTabClosed]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateSourceChosen]: {
    conversationId: string
    templateSourceType: TemplateSource
    isFirstTemplate: boolean
  }
  [AnalyticsEvent.TemplateDriveDialogOpened]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateSourceDialogDismissed]: {
    conversationId: string
  }
  [AnalyticsEvent.TemplateInfluencedFeedback]: {
    conversationId: string
    positive: boolean
  }
  [AnalyticsEvent.OrganizationCreationCtaClicked]: object
  [AnalyticsEvent.OrganizationCreated]: {
    organizationId: string
    organizationCreatedInvitesCount: number
  }
  [AnalyticsEvent.OrganizationInviteAccepted]: {
    organizationId: string
    organizationInviteId: string
    organizationJoinPath: OrganizationJoinPath
  }
  [AnalyticsEvent.OrganizationInviteSent]: {
    organizationId: string
    organizationInviteToEmail: string
    // InviteEmailStatus - the event fires on invite creation regardless of email outcome
    emailStatus: string
  }
  [AnalyticsEvent.OrganizationUserRemoved]: {
    organizationId: string
    removedUserId: string
  }
  [AnalyticsEvent.OrganizationRosterOpened]: {
    organizationId: string
    organizationRosterEntrySource: OrganizationRosterEntrySource
  }
  [AnalyticsEvent.OrganizationLeft]: {
    organizationId: string
  }
  // Research in Assistant – Discovery & Intent
  [AnalyticsEvent.RiaToggleClicked]: {
    riaToggleState: RiaToggleState
    riaToggleName: RiaToggleName
  }
  [AnalyticsEvent.RiaSourcesButtonClicked]: object
  [AnalyticsEvent.RiaSourcesDialogOpened]: {
    riaDialogSource: RiaDialogSource
  }
  // Research in Assistant – Configuration & Submission
  [AnalyticsEvent.RiaSourcesTabChanged]: {
    riaFromTab: RiaTab
    riaToTab: RiaTab
  }
  [AnalyticsEvent.RiaSourcesClearAllClicked]: {
    riaClearedStatesCount: number
    riaClearedFederalCount: number
  }
  [AnalyticsEvent.RiaSourcesApplyClicked]: {
    riaSelectedStatesCount: number
    riaSelectedFederalCount: number
    riaAllFederalSourcesSelected: boolean
    riaIncludeFederalContent: boolean
    riaIncludeUnpublishedCaseLaw: boolean
    riaDateRange: string
    riaFederalAdded: number
    riaFederalRemoved: number
  }
  [AnalyticsEvent.RiaSelectAllFederalChecked]: {
    conversationId: string
  }
  [AnalyticsEvent.RiaSelectAllFederalUnchecked]: {
    conversationId: string
  }
  [AnalyticsEvent.RiaMessageWithResearchSubmitted]: {
    conversationId: string
    riaSelectedStatesCount: number
    riaSelectedFederalCount: number
    riaAllFederalSourcesSelected: boolean
    riaIncludeFederalContent: boolean
    riaIncludeUnpublishedCaseLaw: boolean
    riaDateRange: string
  }
  [AnalyticsEvent.RiaSourceDownloadClicked]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    matterId?: string
  }
  [AnalyticsEvent.RiaSourceViewerTabChanged]: {
    conversationId: string
    riaMessageId: string
    fromTab: RiaViewerTab
    toTab: RiaViewerTab
    matterId?: string
  }
  [AnalyticsEvent.RiaCaseAnalysisRequested]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorSectionExpanded]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection: RiaCitatorSection
    riaCitatorSectionCount?: number
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorCaseSelected]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection: RiaCitatorSection
    riaCitatorCaseId: string
    riaCitatorCaseTitle?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaCitatorRefreshClicked]: {
    conversationId: string
    riaMessageId: string
    riaCitatorSection?: RiaCitatorSection
    riaCitatorRefreshReason?: string
    matterId?: string
  }
  [AnalyticsEvent.RiaSourceOpenInNewTabClicked]: {
    conversationId: string
    riaMessageId: string
    riaSourceCitationId: string
    riaSourceType: string
    riaSourceTriggerLocation: RiaSourceTriggerLocation
    riaSourceTitle?: string
    matterId?: string
  }
  // AI Citator – Case treatment badge engagement. Case-agnostic by design.
  [AnalyticsEvent.CitatorBadgeImpression]: {
    citatorBadgeType: CitatorBadgeType
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorBadgeClicked]: {
    citatorBadgeType: CitatorBadgeType
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorPanelOpened]: {
    citatorPanelSource: CitatorPanelSource
    citatorSurface: CitatorSurface
    citatorOpenedIn: CitatorOpenedIn
  }
  [AnalyticsEvent.CitatorTreatmentOutcomeClicked]: {
    citatorTreatmentOutcome: CitatorTreatmentOutcome
    citatorSurface: CitatorSurface
  }
  [AnalyticsEvent.CitatorReferenceGroupExpanded]: {
    citatorSurface: CitatorSurface
    citatorContainsBadges: boolean
  }
  // High-Fidelity Template Adherence – properties are feature-prefixed (Amplitude property names share a global namespace)
  [AnalyticsEvent.ArtifactTemplateSelected]: {
    artifactVersionId: string
    conversationId: string
    matterId: string
    artifactTemplateSource: ArtifactTemplateSource
    isTemplateReplacement: boolean
  }
  [AnalyticsEvent.ArtifactTemplateApplyCompleted]: {
    artifactVersionId: string
    matterId: string
    artifactExportType: ArtifactExportType
    templateApplyDurationMs: number
  }
  [AnalyticsEvent.ArtifactTemplateApplyFailed]: {
    artifactVersionId: string
    matterId: string
    artifactExportType: ArtifactExportType
    templateApplyDurationMs: number
    templateApplyErrorType: ArtifactTemplateApplyErrorType
  }
  // Browser download only. Save-to-matter stays on ArtifactExportCompleted. feature is attached
  // by useAnalytics, not this payload.
  [AnalyticsEvent.ArtifactDownloadSuccess]: {
    artifactVersionId: string
    artifactId?: string
  }
  [AnalyticsEvent.ArtifactDownloadFailed]: {
    artifactVersionId: string
    artifactId?: string
  }
  // Every successful export (download/save × with/without template). artifactId is the stable
  // draft-level key; optional ids are unavailable in some paths.
  [AnalyticsEvent.ArtifactExportCompleted]: {
    artifactVersionId: string
    artifactId?: string
    artifactExportType: ArtifactExportType
    templateApplied: boolean
    conversationId?: string
    matterId?: string
  }
  // feature is attached by the docx-export helper, not this payload.
  [AnalyticsEvent.DocxExportDownloaded]: {
    userFileId: string
    docxDownloadSurface: DocxDownloadSurface
    exportId: string
  }
  [AnalyticsEvent.DocxExportsFolderOpened]: {
    docxFolderSource: DocxFolderSource
    exportId?: string
    userFileId?: string
    conversationId?: string
  }
  [AnalyticsEvent.DraftEntryPointClicked]: {
    artifactId: string
    artifactVersionId: string
    conversationId: string
    matterId: string
    draftEntryPoint: DraftEntryPoint
  }
  // feature inheritance is suppressed by the settings helper. No feature value is set.
  [AnalyticsEvent.SettingsModalViewed]: {
    settingsSection: SettingsModalSection
    settingsEntrySource: SettingsEntrySource
    settingsViewTrigger: SettingsViewTrigger
  }
  [AnalyticsEvent.DefaultJurisdictionConfigureClicked]: {
    hasExistingDefaultJurisdiction: boolean
  }
  [AnalyticsEvent.CustomInstructionsSaved]: {
    characterCount: number
    isCleared: boolean
  }
  [AnalyticsEvent.DefaultJurisdictionSaved]: {
    defaultJurisdictionId: string
    isStateDefaultJurisdiction: boolean
    isFederalDefaultJurisdiction: boolean
    isDefaultJurisdictionUndecided: boolean
    defaultStateJurisdiction: string
    defaultFederalJurisdiction: string
  }
  [AnalyticsEvent.MatterJurisdictionTooltipLinkClicked]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
  [AnalyticsEvent.MatterJurisdictionCtaViewed]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
  [AnalyticsEvent.MatterJurisdictionCtaClicked]: {
    matterId: string
    matterCreatedBeforeLaunch: boolean
  }
  // Continuous Matter Drive Sync – observability only, see analytics/README.md ("Matter Drive Sync").
  // `feature: 'matter_drive_sync'` is required on every event below; call sites get it for free via
  // the `trackMatterDriveSyncEvent` wrapper (src/analytics/utils/matter-drive-sync-analytics.ts),
  // which also suppresses route-derived feature inheritance (e.g. 'agent' on chat routes).
  // `externalStorageProvider` reuses the shared `ExternalStorageProvider` union, which includes
  // `clio` — Clio is intentionally included here rather than narrowed to DAT-12's original 4
  // providers; see analytics/README.md ("Matter Drive Sync") for the rationale.
  [AnalyticsEvent.MatterDriveSyncSetupEntryClicked]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider?: ExternalStorageProvider
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  // Pre-connection discovery (e.g. "Connect Drive" → Settings). Distinct from SetupEntryClicked.
  // `matterId` is optional when there is no active matter.
  [AnalyticsEvent.MatterDriveSyncDiscoveryClicked]: {
    feature: 'matter_drive_sync'
    organizationId: string
    matterId?: string
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncAccountConnectModalOpened]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
  }
  [AnalyticsEvent.MatterDriveSyncConnectRedirectStarted]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
  }
  [AnalyticsEvent.MatterDriveSyncConnectRedirectFailed]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterDriveSyncAccountConnected]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
  }
  [AnalyticsEvent.MatterDriveSyncAccountDisconnected]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
  }
  [AnalyticsEvent.MatterDriveSyncAccountDisconnectFailed]: {
    feature: 'matter_drive_sync'
    externalStorageProvider: ExternalStorageProvider
    organizationId: string
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.MatterDriveSyncFolderPickerOpened]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    replaceExistingMapping: boolean
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncFolderBrowseFailed]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    folderPickerBreadcrumbDepth: number
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncFolderPickerDismissed]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    replaceExistingMapping: boolean
    matterDriveSyncDismissReason: MatterDriveSyncDismissReason
    hadFolderSelected: boolean
    folderPickerBreadcrumbDepth: number
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncFolderMapped]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    externalSyncMappingId: string
    createdMapping: boolean
    replaceExistingMapping: boolean
    observedFilesCount: number
    createdFileLinksCount: number
    observedFoldersCount: number
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncFolderMapFailed]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    replaceExistingMapping: boolean
    errorType: string
    statusCode?: number
    matterDriveSyncEntrySource: MatterDriveSyncEntrySource
  }
  [AnalyticsEvent.MatterDriveSyncFolderDisconnected]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
  }
  [AnalyticsEvent.MatterDriveSyncFolderDisconnectFailed]: {
    feature: 'matter_drive_sync'
    matterId: string
    organizationId: string
    externalStorageProvider: ExternalStorageProvider
    errorType: string
    statusCode?: number
  }
  [AnalyticsEvent.UserFileUnsupportedTypeRejected]: {
    fileExtension: string
    mediaKind: UnsupportedUploadMediaKind
  }
  [AnalyticsEvent.TableCreated]: {
    feature: 'tables'
    tableId: string
    matterId: string
    tableCreationMethod: TableCreationMethod
    documentCount?: number
  }
  [AnalyticsEvent.TableOpened]: {
    feature: 'tables'
    tableId: string
    matterId: string
    tableEntrySource: TableEntrySource
    documentAnalysis?: boolean
  }
  [AnalyticsEvent.TableRenamed]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableDeleted]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableEdited]: {
    feature: 'tables'
    tableId: string
    tableEditAction: TableEditAction
    tableEditSource: TableEditSource
  }
  [AnalyticsEvent.TableDocumentsAdded]: {
    feature: 'tables'
    tableId: string
    documentCount: number
    tableDocumentSource: TableDocumentSource
  }
  [AnalyticsEvent.TableFiltersApplied]: {
    feature: 'tables'
    tableId: string
    filterCount: number
    filterMatch: TableFilterMatch
  }
  [AnalyticsEvent.TableCopyRequested]: {
    feature: 'tables'
    tableId: string
    tableCopyMethod: TableCopyMethod
    selectedCellCount?: number
    withFootnotes?: boolean
  }
  [AnalyticsEvent.TableDownloaded]: {
    feature: 'tables'
    tableId: string
    tableDownloadScope: TableDownloadScope
  }
  [AnalyticsEvent.TableColumnHidden]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableColumnsClicked]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableSortClicked]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableAssistantOpened]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableAssistantClosed]: {
    feature: 'tables'
    tableId: string
  }
  [AnalyticsEvent.TableReferenceClicked]: {
    feature: 'tables'
    tableId: string
    tableReferenceId: string
  }
  [AnalyticsEvent.ReferenceHighlightMatchStatus]: {
    citationHighlightsImprovements: boolean
    viewer: 'markdown' | 'docx'
    /** The file's content type as detected from its contents at upload, or 'unknown'. */
    fileType: string
    passageCount: number
    fullCount: number
    partialCount: number
    noneCount: number
    matchedChunkCount: number
    totalChunkCount: number
    repeatedPassageCount: number
  }
}
